/**
 * Slider — frontend Swiper initialization.
 *
 * Reads data attributes from the PHP-rendered wrapper and bootstraps
 * Swiper with slide effect, Navigation, Pagination, optional Autoplay and A11y.
 *
 * Depends on the global `window.WombatSwiper` (registered as `wombat-swiper`).
 *
 * @package
 */

/**
 * Set the --slider-offset CSS custom property on each slider block so that
 * `height: calc(100dvh - var(--slider-offset))` keeps the bar above the fold.
 *
 * Accounts for sticky headers — the offset is measured from the bottom edge
 * of any sticky/fixed header to ensure the slider fills exactly the visible
 * viewport area below it.
 *
 * @param {NodeList|Array} blocks Slider block elements.
 */
const setSliderOffsets = ( blocks ) => {

	// Determine the bottom edge of a sticky/fixed header (if any).
	const header = document.querySelector( "header" );
	let headerBottom = 0;

	if ( header ) {

		const pos = window.getComputedStyle( header ).position;

		if ( pos === "sticky" || pos === "fixed" ) {

			headerBottom = header.getBoundingClientRect().bottom;

		}

	}

	blocks.forEach( ( block ) => {

		// Document-level top of the block.
		const blockTop = block.getBoundingClientRect().top + window.scrollY;

		// Use whichever is larger: the block's own position or the
		// sticky header's bottom edge (when the block tucks under it).
		const offset = Math.max( blockTop, headerBottom );
		block.style.setProperty( "--slider-offset", `${Math.round( offset )}px` );

	} );

};

/**
 * Initialise Swiper instances for all slider blocks on the page.
 */
const initSliders = () => {

	const { Swiper, Pagination, Navigation, Autoplay, A11y } =
		window.WombatSwiper;

	/** @type {NodeListOf<HTMLElement>} */
	const blocks = document.querySelectorAll( ".b-slider" );

	// Set offset before Swiper init so the first paint is correct.
	setSliderOffsets( blocks );

	// Recalculate offsets on resize (debounced).
	let resizeTimer;

	window.addEventListener( "resize", () => {

		clearTimeout( resizeTimer );
		resizeTimer = setTimeout( () => setSliderOffsets( blocks ), 150 );

	} );

	blocks.forEach( ( block ) => {

		const swiperEl = block.querySelector( ".js-slider-swiper" );

		if ( !swiperEl ) {

			return;

		}

		const isAutoplay = block.dataset.autoplay === "true";
		const delay = parseInt( block.dataset.delay, 10 ) || 5000;
		const barContentEl = block.querySelector( ".js-slider-bar-content" );
		const titleEl = block.querySelector( ".js-slider-title" );
		const subtitleEl = block.querySelector( ".js-slider-subtitle" );
		let barTransitionTimer;

		/**
		 * Update bar text and animate fade between slide changes.
		 *
		 * @param {Object} swiper  Swiper instance.
		 * @param {boolean} animate Whether to animate text transition.
		 */
		const updateBarContent = ( swiper, animate = true ) => {

			if ( !barContentEl || !titleEl || !subtitleEl ) {

				return;

			}

			const activeSlide = swiper.slides[swiper.activeIndex];

			if ( !activeSlide ) {

				return;

			}

			const nextTitle = activeSlide.dataset.slideTitle || "";
			const nextSubtitle = activeSlide.dataset.slideSubtitle || "";

			/**
			 * Apply active slide text values to the bottom bar.
			 */
			const applyText = () => {

				titleEl.textContent = nextTitle;
				subtitleEl.textContent = nextSubtitle;
				titleEl.style.display = nextTitle ? "block" : "none";
				subtitleEl.style.display = nextSubtitle ? "block" : "none";

			};

			window.clearTimeout( barTransitionTimer );

			if ( !animate ) {

				barContentEl.classList.remove(
					"b-slider__bar-content--is-fading",
				);
				applyText();
				return;

			}

			barContentEl.classList.add( "b-slider__bar-content--is-fading" );

			barTransitionTimer = window.setTimeout( () => {

				applyText();
				barContentEl.classList.remove(
					"b-slider__bar-content--is-fading",
				);

			}, 200 );

		};

		/** @type {Array} Swiper modules to use. */
		const modules = [Pagination, Navigation, A11y];

		/**
		 * Handle Swiper init event.
		 *
		 * @param {Object} swiper Swiper instance.
		 */
		const handleInit = ( swiper ) => {

			updateBarContent( swiper, false );

		};

		/**
		 * Handle Swiper slide transition start event.
		 *
		 * @param {Object} swiper Swiper instance.
		 */
		const handleSlideChangeTransitionStart = ( swiper ) => {

			updateBarContent( swiper );

		};

		if ( isAutoplay ) {

			modules.push( Autoplay );

		}

		/** @type {Object} Swiper configuration. */
		const config = {
			modules,
			loop: true,
			speed: 600,
			slidesPerView: 1,
			pagination: {
				el: block.querySelector( ".b-slider__pagination" ),
				clickable: true,
				bulletClass: "b-slider__dot",
				bulletActiveClass: "b-slider__dot--active",
			},
			navigation: {
				prevEl: block.querySelector( ".b-slider__arrow--prev" ),
				nextEl: block.querySelector( ".b-slider__arrow--next" ),
			},
			a11y: {
				enabled: true,
				prevSlideMessage: "Poprzedni slajd",
				nextSlideMessage: "Następny slajd",
				paginationBulletMessage: "Przejdź do slajdu {{index}}",
			},
			on: {
				init: handleInit,
				slideChangeTransitionStart: handleSlideChangeTransitionStart,
			},
		};

		if ( isAutoplay ) {

			config.autoplay = {
				delay,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			};

		}

		// eslint-disable-next-line no-new
		new Swiper( swiperEl, config );

	} );

};

if ( document.readyState === "loading" ) {

	document.addEventListener( "DOMContentLoaded", initSliders );

} else {

	initSliders();

}
